import { ArrowDownWideNarrow, MessageCircle, Plus, UserX } from "lucide-react";
import { colorOf } from "@/lib/colors";
import { todayIso } from "@/lib/dates";
import { isManual } from "@/lib/order";
import { SortableList } from "./SortableList";
import { EmployeeAvatar } from "./EmployeeBadge";
import { AbsentBadge } from "./AbsentBadge";

const DayChips = ({ days, value, onChange }) => (
  <div className="-mx-3 flex gap-2 overflow-x-auto px-3 pb-1" data-testid="mobile-day-chips">
    {[{ idx: "all", short: "Tutti", label: "settimana" }, ...days].map((d) => {
      const active = String(d.idx) === value;
      return (
        <button
          key={d.idx}
          type="button"
          data-testid={`mobile-day-chip-${d.idx}`}
          onClick={() => onChange(String(d.idx))}
          className={`day-chip ${active ? "is-active" : ""} ${d.iso === todayIso() ? "is-today" : ""}`}
        >
          <span className="font-display text-sm font-semibold">{d.short}</span>
          <span className="text-[11px] opacity-70">{d.label}</span>
        </button>
      );
    })}
  </div>
);

const Slot = ({ emp, day, tasks, h }) => {
  const c = colorOf(emp);
  const absence = h.absenceFor(emp.id, day.iso);
  const slot = `${emp.id}-${day.idx}`;
  return (
    <div data-testid={`mobile-slot-${slot}`} className="mobile-slot" style={{ background: c.soft, borderColor: c.border, boxShadow: `inset 4px 0 0 ${c.accent}` }}>
      <div className="flex items-center gap-2">
        <EmployeeAvatar employee={emp} size={30} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-sm font-semibold" style={{ color: c.ink }}>{emp.name}</p>
          <p className="text-[11px] text-slate-500">{absence ? "Assente" : `${tasks.length} attività`}</p>
        </div>
        {isManual(tasks) && (
          <button
            type="button"
            data-testid={`mobile-sort-${slot}`}
            onClick={() => h.resetOrder(tasks)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/70 text-slate-500 shadow-sm"
            aria-label="Ordina per priorità"
          >
            <ArrowDownWideNarrow className="h-4 w-4" />
          </button>
        )}
        {!absence && (
          <button
            type="button"
            data-testid={`mobile-absent-btn-${slot}`}
            onClick={() => h.markAbsent(emp, day)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/70 text-slate-500 shadow-sm"
            aria-label={`Segna ${emp.name} assente`}
          >
            <UserX className="h-4 w-4" />
          </button>
        )}
        {tasks.length > 0 && (
          <button
            type="button"
            data-testid={`mobile-share-${emp.id}-${day.idx}`}
            onClick={() => h.shareDay(emp, day)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm"
            aria-label={`Invia la giornata di ${emp.name} su WhatsApp`}
          >
            <MessageCircle className="h-4 w-4" />
          </button>
        )}
        {!absence && (
          <button
            type="button"
            data-testid={`mobile-add-${emp.id}-${day.idx}`}
            onClick={() => h.add(emp.id, day.iso)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm"
            style={{ color: c.ink }}
            aria-label={`Aggiungi attività per ${emp.name}`}
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>
      {absence && (
        <div className="mt-2.5">
          <AbsentBadge testId={`mobile-absent-${slot}`} removeTestId={`mobile-unabsent-${slot}`} onRemove={() => h.removeAbsence(absence, emp, day)} />
        </div>
      )}
      {tasks.length > 0 && (
        <div className="mt-2.5 flex flex-col gap-1.5">
          <SortableList tasks={tasks} h={h} />
        </div>
      )}
    </div>
  );
};

export const MobileDayView = ({ days, visibleDays, employees, tasksFor, dayFilter, setDay, h }) => (
  <div data-testid="mobile-day-view" className="space-y-4">
    <DayChips days={days} value={dayFilter} onChange={setDay} />
    {visibleDays.map((d) => (
      <section key={d.iso} className="space-y-2">
        <h3 className="flex items-baseline gap-2 px-1 font-display text-base font-semibold">
          {d.name} <span className="text-xs font-normal text-slate-500">{d.label}</span>
        </h3>
        {employees.map((emp) => (
          <Slot key={emp.id} emp={emp} day={d} tasks={tasksFor(emp.id, d.iso)} h={h} />
        ))}
      </section>
    ))}
  </div>
);
